/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DoCheck, Inject, OnInit, ViewChild, signal} from '@angular/core';
import {NgForm} from '@angular/forms';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';

import template from './order-budget-modal.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {
  addSecondForUi,
  getBudgetNetworkTimezone,
  isBudgetPeriodEditable,
  networkDayFromApiDate,
  networkDayFromPickerDate,
  normalizeBudgetDay,
  pickerDateForNetworkDay,
  utcApiDateFromNetworkPicker
} from './order-budget-utils';
import {ResourceHelper} from '../../components/util/resource-helper';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {ChartOptionsUtil} from '../common/options/chart-options-util.service';
import {deferSignalStateWrite} from '../common/util/template-state';

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-order-budget-modal',
  template
})
export class OrderBudgetModalComponent implements OnInit, DoCheck {
  @ViewChild('modalForm') modalForm: NgForm;

  modelObject: any;
  moneyCurrency: any;
  objTranslation = 'Order';
  onComplete: (result: {obj?: any}) => void;
  persistFields: (modelObject: any, fields: string[]) => Promise<any>;

  budget: any[] = [];
  budgetRollover = false;
  changesDates = false;
  readonly validationErrors = signal<any[]>([]);
  readonly disabled = signal(false);

  private readonly saveDisabledState = signal(true);

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any
  ) {
  }

  /** Keeps Save disabled while invalid; buffers form/disabled settle to avoid NG0100. */
  get saveDisabled(): boolean {
    return this.saveDisabledState();
  }

  ngOnInit(): void {
    this.ensureModelModDates();
    this.budgetRollover = this.modelObject.budgetRollover;
    this.budget = _.cloneDeep(this.modelObject.budget) || [];

    if (this.budget.length === 0) {
      this.budget.push(this.addFromScratch());
    } else if (this.modelObject.startDate) {
      const networkStartDay = networkDayFromApiDate(this.modelObject.startDate, this.adnTimeShifter);
      this.budget[0].startDay = this.budget[0].startDay || networkStartDay;
      if (this.modelObject.endDate) {
        const networkEndDay = networkDayFromApiDate(this.modelObject.endDate, this.adnTimeShifter);
        this.budget[this.budget.length - 1].endDay = this.budget[this.budget.length - 1].endDay || networkEndDay;
      }
    }

    _.forEach(this.budget, (b) => {
      b.startDay = normalizeBudgetDay(b.startDay, this.adnTimeShifter, this.networkTimezone) || b.startDay;
      b.endDay = normalizeBudgetDay(b.endDay, this.adnTimeShifter, this.networkTimezone) || b.endDay;
      this.ensureBudgetAmount(b);
      this.updateTimes(b);
    });

    this.budget = addSecondForUi(this.budget);
    this.checkDatesChange();
  }

  ngDoCheck(): void {
    const form = this.modalForm;
    const saveDisabled = this.disabled()
      || !form
      || !!(form.pending || (form.invalid && !form.errors?.['validation']));
    if (saveDisabled !== this.saveDisabledState()) {
      this.saveDisabledState.set(saveDisabled);
    }
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  onDateChange(): void {
    _.forEach(this.budget, (b) => {
      if (b.modStartDay) {
        b.startDay = networkDayFromPickerDate(b.modStartDay, this.adnTimeShifter);
      }
      if (b.modEndDay) {
        b.endDay = networkDayFromPickerDate(b.modEndDay, this.adnTimeShifter);
      }
      this.updateTimes(b);
    });
    this.checkDatesChange();
  }

  addBudgetPeriod(): void {
    if (this.budget.length === 0) {
      this.budget.push(this.addFromScratch());
      this.checkDatesChange();
      return;
    }
    const refBudget = this.budget[this.budget.length - 1];
    const adjustedStartDate = moment.tz(refBudget.endDay, this.networkTimezone).add(1, 'day').format('YYYY-MM-DD');

    let adjustedMomentEndDate = moment.tz(adjustedStartDate, this.networkTimezone).endOf('month');
    const current = this.adnTimeShifter.getMomentInNetworkTimezone();
    if (adjustedMomentEndDate.isBefore(current)) {
      adjustedMomentEndDate = current.clone().endOf('month');
    }
    const adjustedEndDate = adjustedMomentEndDate.format('YYYY-MM-DD');
    const budget = {
      startDay: adjustedStartDate,
      endDay: adjustedEndDate
    };
    this.ensureBudgetAmount(budget);
    this.updateTimes(budget);
    this.budget.push(budget);
    this.budget = addSecondForUi(this.budget);
    this.checkDatesChange();
  }

  removePeriod(index: number): void {
    _.pullAt(this.budget, index);
    this.checkDatesChange();
  }

  updateBudgets(): void {
    if (this.modalForm?.invalid) {
      this.modalForm.control.markAllAsTouched();
      return;
    }

    this.modelObject.budget = _.cloneDeep(this.budget);

    const firstBudgetPeriod = _.cloneDeep(_.get(this.modelObject.budget, [0]));
    const endBudgetPeriod = _.cloneDeep(_.get(this.modelObject.budget, [this.modelObject.budget.length - 1]));
    const matches = this.checkDatesChange();

    _.forEach(this.modelObject.budget, (b) => {
      b.startDay = networkDayFromPickerDate(b.modStartDay, this.adnTimeShifter);
      b.endDay = networkDayFromPickerDate(b.modEndDay, this.adnTimeShifter);

      ResourceHelper.checkMoney(b, ['amount']);
      delete b.modStartDay;
      delete b.modEndDay;
      delete b.nextPeriodOption;
    });

    const fieldsToSave = ['budget', 'budgetRollover'];
    if (this.modelObject.budget.length > 0) {
      if (this.modelObject.startDate && !matches.startDateMatch) {
        this.modelObject.startDate = utcApiDateFromNetworkPicker(firstBudgetPeriod.modStartDay, this.adnTimeShifter, 'start');
        fieldsToSave.push('startDate');
      }
      if (this.modelObject.endDate && !matches.endDateMatch) {
        this.modelObject.endDate = utcApiDateFromNetworkPicker(endBudgetPeriod.modEndDay, this.adnTimeShifter, 'end');
        fieldsToSave.push('endDate');
      }
    }
    const budgetObj = _.get(this.modelObject, ['objectives', 'BUDGET']) || {};
    if (this.modelObject.budget.length > 0 && budgetObj.amount && budgetObj.currency) {
      this.modelObject.objectives.BUDGET = null;
      fieldsToSave.push('objectives');
    }

    this.modelObject.budgetRollover = this.budgetRollover;

    this.disabled.set(true);
    this.validationErrors.set([]);
    this.adnModalLoading.applyModalLoading();
    this.persistFields(this.modelObject, fieldsToSave).then((savedObj: any) => {
      // Defer — sync disabled true→false mid-verify NG0100s [disabled]="saveDisabled".
      deferSignalStateWrite(() => {
        this.disabled.set(false);
        this.adnModalLoading.removeModalLoading();
        this.onComplete?.({obj: savedObj});
        this.bsModalRef.hide();
      });
    }, (errors: any) => {
      const nextErrors = _.get(errors, ['data', 'errors'], []);
      // Defer — [errors]="validationErrors" flipping []→messages mid-CD is NG0100
      // and can prevent the messages from rendering.
      deferSignalStateWrite(() => {
        this.disabled.set(false);
        this.validationErrors.set(nextErrors);
        this.adnModalLoading.removeModalLoading();
      });
    });
  }

  private get networkTimezone(): string {
    return getBudgetNetworkTimezone(this.adnTimeShifter, this.ChartOptionsUtil);
  }

  private ensureModelModDates(): void {
    if (!this.modelObject.modStartDate && this.modelObject.startDate) {
      this.modelObject.modStartDate = this.modelObject.startDate;
    }
    if (!this.modelObject.modEndDate && this.modelObject.endDate) {
      this.modelObject.modEndDate = this.modelObject.endDate;
    }
  }

  private ensureBudgetAmount(budget: any): any {
    if (!budget.amount || !_.isPlainObject(budget.amount)) {
      budget.amount = {amount: null, currency: this.moneyCurrency || null};
    }
    return budget;
  }

  private updateTimes(budget: any): void {
    if (budget.startDay) {
      budget.modStartDay = pickerDateForNetworkDay(budget.startDay, this.networkTimezone, 'start');
    }
    if (budget.endDay) {
      budget.modEndDay = pickerDateForNetworkDay(budget.endDay, this.networkTimezone, 'end');
    }
    budget.editable = isBudgetPeriodEditable(budget.startDay, budget.endDay, this.networkTimezone);
  }

  private addFromScratch(): any {
    const modStartDate = this.modelObject.modStartDate || this.modelObject.startDate;
    const modEndDate = this.modelObject.modEndDate || this.modelObject.endDate;
    const startDate = modStartDate
      ? networkDayFromApiDate(modStartDate, this.adnTimeShifter)
      : this.adnTimeShifter.getMomentInNetworkTimezone().format('YYYY-MM-DD');
    const endDate = modEndDate
      ? networkDayFromApiDate(modEndDate, this.adnTimeShifter)
      : this.adnTimeShifter.getMomentInNetworkTimezone().endOf('month').format('YYYY-MM-DD');
    const budget: any = {
      startDay: startDate,
      endDay: endDate,
      editable: true
    };
    const budgetObj = _.get(this.modelObject, ['objectives', 'BUDGET']) || {};
    if (_.isFinite(budgetObj.amount) && budgetObj.amount > 0) {
      budget.amount = budgetObj;
    }
    this.ensureBudgetAmount(budget);
    this.updateTimes(budget);
    return budget;
  }

  private checkDatesChange(): {startDateMatch: boolean; endDateMatch: boolean} {
    this.changesDates = false;
    const matches = {
      startDateMatch: false,
      endDateMatch: false
    };
    if (this.budget.length < 1) {
      return matches;
    }
    const modStartDate = this.modelObject.modStartDate || this.modelObject.startDate;
    const modEndDate = this.modelObject.modEndDate || this.modelObject.endDate;
    if (modStartDate) {
      matches.startDateMatch = networkDayFromApiDate(modStartDate, this.adnTimeShifter)
        === networkDayFromPickerDate(this.budget[0].modStartDay, this.adnTimeShifter);
      this.changesDates = !matches.startDateMatch;
    }
    if (modEndDate) {
      matches.endDateMatch = networkDayFromApiDate(modEndDate, this.adnTimeShifter)
        === networkDayFromPickerDate(this.budget[this.budget.length - 1].modEndDay, this.adnTimeShifter);
      this.changesDates = this.changesDates || !matches.endDateMatch;
    }
    return matches;
  }

}
